// Banner global que aparece no topo do Layout quando ha NFs com problema:
//   - status rejeitada/erro_comunicacao (NFC-e/NF-e)
//   - vendas finalizadas com tipo_documento mas sem NF emitida
// Click leva direto pra aba "Com problemas" em /notas-fiscais.
// Dispensavel por 1 dia via X (igual AlertaContasPagarBanner).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAlertaFaixa } from './alertas/AlertasContexto';

interface Resumo {
  total: number;
  nfs_com_erro: number;
  vendas_sem_nf: number;
}

const POLLING_MS = 5 * 60 * 1000;
const KEY_DISMISS = 'notas_problemas.alerta.dismiss';

function dismissedHoje(): boolean {
  const v = localStorage.getItem(KEY_DISMISS);
  if (!v) return false;
  return v === new Date().toISOString().slice(0, 10);
}

export function AlertaNotasComProblemasBanner() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [dismissed, setDismissed] = useState(dismissedHoje);

  const podeVer = usuario && ['admin', 'gerente', 'caixa'].includes(usuario.perfil);

  useEffect(() => {
    if (!podeVer) return;
    let cancelado = false;
    async function tick() {
      try {
        const r = await api.get<Resumo>('/api/notas-fiscais/problemas-resumo');
        if (!cancelado) setResumo(r);
      } catch {}
    }
    void tick();
    const id = setInterval(tick, POLLING_MS);
    return () => { cancelado = true; clearInterval(id); };
  }, [podeVer]);

  const fundo = '#ef4444';
  const borda = '#b91c1c';
  const texto = '#000';

  const ativo = !!(podeVer && !dismissed && resumo && resumo.total > 0);
  const recolhido = useAlertaFaixa(
    'notas-problemas',
    ativo ? { rotulo: 'Notas com problema', bg: fundo, borda, qtd: resumo!.total } : null,
  );

  if (!ativo || recolhido || !resumo) return null;

  function fechar() {
    localStorage.setItem(KEY_DISMISS, new Date().toISOString().slice(0, 10));
    setDismissed(true);
  }

  return (
    <div
      style={{
        background: fundo,
        borderLeft: `4px solid ${borda}`,
        color: texto,
        padding: '8px 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 13,
        cursor: 'pointer',
      }}
      onClick={() => navigate('/notas-fiscais?aba=problemas')}
    >
      <AlertTriangle size={16} />
      <span style={{ fontWeight: 600 }}>Notas com problema:</span>
      {resumo.nfs_com_erro > 0 && (
        <span>{resumo.nfs_com_erro} NF{resumo.nfs_com_erro === 1 ? '' : 's'} rejeitada{resumo.nfs_com_erro === 1 ? '' : 's'}/com erro</span>
      )}
      {resumo.nfs_com_erro > 0 && resumo.vendas_sem_nf > 0 && <span>·</span>}
      {resumo.vendas_sem_nf > 0 && (
        <span>{resumo.vendas_sem_nf} venda{resumo.vendas_sem_nf === 1 ? '' : 's'} sem NF emitida</span>
      )}
      <span style={{ marginLeft: 'auto', textDecoration: 'underline' }}>Resolver</span>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); fechar(); }}
        title="Dispensar ate amanha"
        style={{ background: 'transparent', border: 'none', color: texto, cursor: 'pointer', padding: 2 }}
      >
        <X size={14} />
      </button>
    </div>
  );
}
